
import axios from 'axios'

import { AuthResponse } from '../redux/authSlice/authSlice'

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL

// Prevent multiple simultaneous redirects
let isRedirectingToLogin = false

// auth api instance
const authInstance = axios.create({
  baseURL: API_BASE_URL
})

// data api instance
const dataInstance = axios.create({
  baseURL: API_BASE_URL
})

// auth api response interceptor
authInstance.interceptors.response.use(function (response) {
    const token = response.data.accessToken

    if (token) {
      localStorage.setItem('user', JSON.stringify([response.data]))
    }

    return response
  },
  function (error) {
    return Promise.reject(error)
  }
)

// data api request interceptor
dataInstance.interceptors.request.use(
  function (config) {
    const stringifyData = localStorage.getItem('user')

    if (stringifyData) {
      try {
        const parsedData = JSON.parse(stringifyData)
        const user: AuthResponse | undefined = Array.isArray(parsedData)
          ? parsedData[0]
          : parsedData

        if (user?.accessToken) {
          config.headers['x-access-token'] = user.accessToken
        }
      } catch {
        // Remove corrupted authentication data
        localStorage.removeItem('user')
      }
    }

    if (
      typeof FormData === 'undefined' ||
      !(config.data instanceof FormData)
    ) {
      config.headers['Content-Type'] = 'application/json'
    }

    return config
  },
  function (error) {
    return Promise.reject(error)
  }
)

// Centralized session expiration handling
dataInstance.interceptors.response.use(
  function (response) {
    return response
  },
  function (error) {
    const status = error.response?.status

    const message = String(
      error.response?.data?.message ??
      error.response?.data?.error ??
      ''
    )

    const normalizedMessage = message.toLowerCase()

    // Preserve existing deleted-account handling
    const accountDeleted =
      status === 403 &&
      normalizedMessage.includes(
        'your account has been deleted by the administrator as per your request'
      )

    // Recognize explicit token-related 403 responses.
    // Do not treat every 403 as an expired token.
    const invalidToken403 =
      status === 403 &&
      /token expired|jwt expired|invalid token|failed to authenticate token|no token provided|token has expired|unauthorized/.test(
        normalizedMessage
      )

    // 401 usually means the authentication is invalid or expired.
    const authenticationFailed =
      status === 401 || invalidToken403

    if (accountDeleted || authenticationFailed) {
      const storedUser = localStorage.getItem('user')

      // Avoid redirecting public/guest requests when no session exists,
      // except when the backend explicitly reports account deletion.
      if (storedUser || accountDeleted) {
        if (!isRedirectingToLogin) {
          isRedirectingToLogin = true

          localStorage.removeItem('user')
          localStorage.removeItem('token')

          localStorage.setItem(
            'logoutMessage',
            accountDeleted
              ? message
              : 'Your session has expired. Please log in again.'
          )

          window.location.href = '/Login'
        }
      }
    }

    return Promise.reject(error)
  }
)

export { authInstance, dataInstance }
